import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { BREAKPOINTS } from 'src/styles/breakpoints';

type MobileNavContextValue = {
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
};

const MobileNavContext = createContext<MobileNavContextValue | null>(null);

export const MobileNavProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);
  const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const toggleSidebar = useCallback(() => setIsSidebarOpen((v) => !v), []);

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${BREAKPOINTS.md + 1}px)`);
    const onChange = () => {
      if (mq.matches) closeSidebar();
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [closeSidebar]);

  useEffect(() => {
    if (!isSidebarOpen) {
      return;
    }
    const mq = window.matchMedia(`(max-width: ${BREAKPOINTS.md}px)`);
    if (mq.matches) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [isSidebarOpen]);

  const value = useMemo(
    () => ({ isSidebarOpen, openSidebar, closeSidebar, toggleSidebar }),
    [isSidebarOpen, openSidebar, closeSidebar, toggleSidebar],
  );

  return <MobileNavContext.Provider value={value}>{children}</MobileNavContext.Provider>;
};

export function useMobileNav() {
  const ctx = useContext(MobileNavContext);
  if (!ctx) {
    throw new Error('useMobileNav must be used within MobileNavProvider');
  }
  return ctx;
}
